import * as React from 'react';
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import {
  AdminDataOrganisationDTO,
  ApiResponse,
  OrganisationApi,
  OrganisationDTO,
  PatchDocument,
} from '../../../api/core';
import { getAuthMiddleware } from '../../../Auth/utils/GetAuthMiddleware';
import { AuthContext } from '../../../Auth/components/AuthProvider';
import FormOrganisation from './FormOrganisation';
import { Blocker, NavigateFunction, Params, useBlocker, useLocation, useNavigate, useParams } from 'react-router';
import compare, { Patch } from '@mms-v3/patch-lib';
import { getDefaultOrganisation } from '../../../Settings/emptyModels';
import { ToastContext, ToastContextType } from '../../../util/providers/ToastProvider';
import { Loading } from '../../system/Loading';
import { IntlShape, useIntl } from 'react-intl';

export function EditOrganisationPage(): ReactNode {
  const navigate: NavigateFunction = useNavigate();
  const location = useLocation();
  const params: Readonly<Params<string>> = useParams();
  const {actions} = useContext(AuthContext);
  const organisationApi: OrganisationApi = new OrganisationApi().withMiddleware(getAuthMiddleware(actions));
  const [blockRequested, setBlockRequested] = React.useState<boolean>(false);
  const blocker: Blocker = useBlocker(useCallback((): boolean => blockRequested, [blockRequested]));
  const toast: ToastContextType = useContext(ToastContext);
  const intl: IntlShape = useIntl();

  const [organisation, setOrganisation] = useState<OrganisationDTO>();
  const [modifiedModel, setModifiedModel] = useState<OrganisationDTO>();
  const [saving, setSaving] = useState<boolean>(false);

  const updateField = (field: keyof OrganisationDTO, value: any): void => {
    setBlockRequested(true);
    setModifiedModel((state: OrganisationDTO): OrganisationDTO => ({...state, [field]: value}));
  };

  const updateAdminField = (field: keyof AdminDataOrganisationDTO, value: any): void => {
    setBlockRequested(true);
    setModifiedModel((state: OrganisationDTO): OrganisationDTO => ({...state, adminDataOrganisation: {...state.adminDataOrganisation, [field]: value},}));
  };

  useEffect((): void => {
    if (blocker.state === 'blocked') {
      if (!blockRequested) return blocker.proceed();
      toast.confirm({
        header: intl.formatMessage({id: 'popup.unsavedInput.pageChange.headline'}),
        message: intl.formatMessage({id: 'popup.unsavedInput.pageChange.message'}),
        icon: 'pi pi-info-circle',
        defaultFocus: 'reject',
        acceptClassName: 'p-button-danger',
        acceptLabel: intl.formatMessage({id: 'button.confirm'}),
        accept: (): void => {
          setModifiedModel(structuredClone(organisation));
          blocker.proceed();
        },
        rejectLabel: intl.formatMessage({id: 'button.abort'}),
        reject: (): void => blocker.reset(),
      });
    }
  }, [blocker.state]);

  useEffect((): void => setModifiedModel(structuredClone(organisation)), [organisation]);

  useEffect((): void => {
    if (params.id !== undefined) {
      const id: number = Number(params.id);
      if (isNaN(id)) {
        void navigate('/error/404');
        return;
      }
      void fetchData(id);
    } else {
      setOrganisation(getDefaultOrganisation());
    }
  }, [location.pathname]);

  const submit = async (): Promise<void> => {
    setSaving(true);
    try {
      let response: ApiResponse<OrganisationDTO>;
      if (!modifiedModel.id) {
        response = await organisationApi.createOrganisationRaw({organisationDTO: modifiedModel});
      } else {
        const patch: Patch[] = compare(organisation, modifiedModel);
        response = await organisationApi.updateOrganisationRaw({
          organisationId: modifiedModel.id,
          patchDocument: patch as PatchDocument[],
        });
      }
      if (response.raw.ok) {
        toast.notify({
          severity: 'success',
          summary: intl.formatMessage({id: 'message.saving.success.title'}),
          detail: intl.formatMessage({id: 'message.saving.success.message'}),
        });
        const value: OrganisationDTO = await response.value();
        setBlockRequested(false);
        void navigate(`/organisation/${value.id}`);
        return;
      }
      toast.error(response, 'message.saving.failed');
    } catch (response) {
      toast.error(response.response, 'message.saving.failed');
    }
    setSaving(false);
  };

  const fetchData = async (id: number): Promise<void> => {
    const response: ApiResponse<OrganisationDTO> = await organisationApi.getReducedOrganisationRaw({organisationId: id});
    if (!response.raw.ok) {
      toast.notify({
        severity: 'error',
        summary: intl.formatMessage({id: 'message.loading.failed.title'}),
        detail: intl.formatMessage({id: 'message.loading.failed.message'}),
      });
      return navigate(`/error/${response.raw.status}`);
    }
    setOrganisation(await response.value());
  };
  if (!modifiedModel) {
    return React.createElement(Loading, {});
  }
  return React.createElement(FormOrganisation, {modifiedModel, submit, updateField, updateAdminField, saving});
}

export default EditOrganisationPage;
